/**
 * Notes — markdown capture with tags. The editor is a plain textarea; the
 * preview renders through react-markdown with GFM so tables and task lists
 * work. Saving is explicit rather than on every keystroke, which keeps the
 * write volume sane on a reactive backend.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Plus, Trash2, Pin, PinOff, Eye, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/** First heading or first non-empty line, for notes with no explicit title. */
function derivedTitle(content: string): string {
  const line = content
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!line) return "Untitled note";
  return line.replace(/^#+\s*/, "").slice(0, 80);
}

/**
 * `"work, ideas ,work"` → `["work", "ideas"]`
 *
 * Duplicates matter: `update` replaces every tag link from this list, so a
 * repeated name would insert the same join row twice.
 */
export function parseTagNames(input: string): string[] {
  return [
    ...new Set(
      input
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean),
    ),
  ];
}

/**
 * Note categories, restored from v2. Slugs are ASCII; the label is display
 * only — never key anything by it (AGENTS.md rule 6).
 */
const CATEGORIES = [
  { value: "idea", label: "Idea" },
  { value: "reflection", label: "Reflection" },
  { value: "event", label: "Event" },
] as const;

type NoteCategory = (typeof CATEGORIES)[number]["value"];

function TagChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "text-xs px-2 py-1 rounded border transition-colors",
        active
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
      {count !== undefined && <span className="ml-1 opacity-60">{count}</span>}
    </button>
  );
}

export default function NotesPage() {
  const [activeTagId, setActiveTagId] = useState<Id<"tags"> | null>(null);
  const [activeCategory, setActiveCategory] = useState<NoteCategory | null>(
    null,
  );

  // Tag and category filtering happen on the server. The text search stays on
  // the client, where it answers without a round trip.
  const notes = useQuery(api.notes.list, {
    ...(activeTagId ? { tagId: activeTagId } : {}),
    ...(activeCategory ? { category: activeCategory } : {}),
  });
  const tags = useQuery(api.notes.listTags, {});
  const createNote = useMutation(api.notes.create);
  const updateNote = useMutation(api.notes.update);
  const removeNote = useMutation(api.notes.remove);
  const ensureTag = useMutation(api.notes.ensureTag);

  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [draftTags, setDraftTags] = useState("");
  const [draftCategory, setDraftCategory] = useState<NoteCategory | null>(null);
  const [editingId, setEditingId] = useState<Id<"notes"> | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingTags, setEditingTags] = useState("");

  /** Typed names become ids, creating any tag that does not exist yet. */
  const resolveTagIds = async (input: string): Promise<Id<"tags">[]> =>
    await Promise.all(parseTagNames(input).map((name) => ensureTag({ name })));

  const filtered = (notes ?? []).filter((note) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return (
      note.content.toLowerCase().includes(term) ||
      (note.title ?? "").toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-5 max-w-3xl">
      <h1 className="page-title text-2xl font-semibold">Notes</h1>

      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!draft.trim()) return;
          const content = draft;
          const tagInput = draftTags;
          const category = draftCategory;
          setDraft("");
          setDraftTags("");
          setDraftCategory(null);
          await createNote({
            content,
            ...(category ? { category } : {}),
            tagIds: await resolveTagIds(tagInput),
          });
        }}
        className="space-y-2"
      >
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Capture a thought… markdown works."
          rows={3}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter saves, matching the rest of the app's shortcuts.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <div className="flex items-center gap-2">
          <div className="flex gap-1" role="group" aria-label="Category for the new note">
            {CATEGORIES.map((c) => (
              <TagChip
                key={c.value}
                label={c.label}
                active={draftCategory === c.value}
                onClick={() =>
                  setDraftCategory(draftCategory === c.value ? null : c.value)
                }
              />
            ))}
          </div>
          <Input
            value={draftTags}
            onChange={(e) => setDraftTags(e.target.value)}
            placeholder="Tags, comma separated"
            aria-label="Tags for the new note, comma separated"
            className="h-9 text-sm"
          />
          <Button type="submit" size="sm" disabled={!draft.trim()}>
            <Plus className="w-4 h-4 mr-1.5" />
            Add note
          </Button>
        </div>
      </form>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search notes…"
        aria-label="Search notes"
      />

      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by category">
        <span className="text-xs text-muted-foreground mr-1">Category</span>
        <TagChip
          label="All"
          active={activeCategory === null}
          onClick={() => setActiveCategory(null)}
        />
        {CATEGORIES.map((c) => (
          <TagChip
            key={c.value}
            label={c.label}
            active={activeCategory === c.value}
            onClick={() =>
              setActiveCategory(activeCategory === c.value ? null : c.value)
            }
          />
        ))}
      </div>

      {tags && tags.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label="Filter by tag"
        >
          <TagChip
            label="All"
            active={activeTagId === null}
            onClick={() => setActiveTagId(null)}
          />
          {tags.map((tag) => (
            <TagChip
              key={tag._id}
              label={tag.name}
              count={tag.noteCount}
              active={activeTagId === tag._id}
              onClick={() =>
                setActiveTagId(activeTagId === tag._id ? null : tag._id)
              }
            />
          ))}
        </div>
      )}

      {notes === undefined && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      {notes?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No notes yet.
          </CardContent>
        </Card>
      )}

      {notes && notes.length > 0 && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing matches “{search}”.
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((note) => {
          const editing = editingId === note._id;

          return (
            <Card key={note._id} className={cn(note.pinned && "border-primary/40")}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-start gap-2">
                  <h2 className="text-sm font-medium flex-1 min-w-0 truncate">
                    {note.title ?? derivedTitle(note.content)}
                  </h2>

                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={editing ? "Preview note" : "Edit note"}
                    onClick={() => {
                      if (editing) {
                        setEditingId(null);
                      } else {
                        setEditingId(note._id);
                        setEditingText(note.content);
                        setEditingTags(note.tags.map((t) => t.name).join(", "));
                      }
                    }}
                  >
                    {editing ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <Pencil className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={note.pinned ? "Unpin note" : "Pin note"}
                    onClick={() =>
                      updateNote({ id: note._id, pinned: !note.pinned })
                    }
                  >
                    {note.pinned ? (
                      <PinOff className="w-4 h-4" />
                    ) : (
                      <Pin className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete note"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeNote({ id: note._id })}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {editing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      rows={8}
                      className="font-mono text-xs"
                    />
                    <Input
                      value={editingTags}
                      onChange={(e) => setEditingTags(e.target.value)}
                      placeholder="Tags, comma separated"
                      aria-label="Tags for this note, comma separated"
                      className="h-9 text-sm"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          await updateNote({
                            id: note._id,
                            content: editingText,
                            tagIds: await resolveTagIds(editingTags),
                          });
                          setEditingId(null);
                        }}
                        disabled={!editingText.trim()}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-sm dark:prose-invert max-w-none prose-pre:bg-muted prose-pre:text-foreground">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {note.content}
                    </ReactMarkdown>
                  </div>
                )}

                {note.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {note.tags.map((tag) => (
                      <button
                        key={tag._id}
                        type="button"
                        onClick={() => setActiveTagId(tag._id)}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/60 transition-colors"
                        aria-label={`Show only notes tagged ${tag.name}`}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
